import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextareaAutosize,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import { useSelector } from "react-redux";
import format from "date-fns/format";
import instance from "../../redux/api";
import { CommonPaginationContainer, CommonFilterSidebar } from "../common";
import { CommonButton } from "../common/commonButton";
import {
  cashAllocationLockedUnlocked,
  getCashAllocationLockedUnlocked,
} from "../../Services/api";
import { useStyles } from "../bankTransactions/bankTranscationContainerStyles";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import { commonToast } from "../common";
import { customDateFormat } from "../CashAllocation/utils";
import FilterListIcon from "@mui/icons-material/FilterList";
import "../common/commonPageStyles.css";

export const LockUnlockContainer = () => {
  // Selectors
  const toggle = useSelector((state) => state.toggleSideMenu);
  const { userData } = useSelector((state) => state?.user);

  // Local state
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [unlockRecordCountTextMessage, setUnlockRecordCountTextMessage] =
    useState("");
  //For Date picker
  const [fromDateValue, setFromDateValue] = useState("");
  const [toDateValue, setToDateValue] = useState("");
  const [lockStateResponse, setLockStateResponse] = useState("");
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(true);

  //For comments Dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [currentClickedRow, setCurrentClickedRow] = useState("");
  const [dialogTextAreaChangeValue, setDialogTextAreaChangeValue] =
    useState("");

  //styles
  const classes = useStyles();
  const Container = {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: 0,
    height: "calc(100vh - 180px)",
    maxHeight: "calc(100vh - 180px)",
    overflow: "hidden",
  };

  const lockButtonStyle = {
    color: 'white',
    backgroundColor: '#1672DE',
    borderRadius: '10px',
    padding: '10px 24px',
    fontWeight: '600',
    fontSize: '14px',
    textTransform: 'none',
    minHeight: '40px',
    minWidth: '120px',
    boxShadow: '0 2px 8px rgba(22, 114, 222, 0.2)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: '#1565c0',
      boxShadow: '0 4px 12px rgba(22, 114, 222, 0.3)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&:disabled': {
      backgroundColor: '#CCCCCC',
      color: '#ffffff',
      boxShadow: 'none',
      cursor: 'not-allowed',
    },
  };

  const cancelButtonStyle = {
    color: '#1672DE',
    backgroundColor: 'transparent',
    border: '1px solid #1672DE',
    borderRadius: '10px',
    padding: '10px 24px',
    fontWeight: '600',
    fontSize: '14px',
    textTransform: 'none',
    minHeight: '40px',
    minWidth: '120px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: 'rgba(22, 114, 222, 0.05)',
      borderColor: '#1565c0',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&:disabled': {
      backgroundColor: '#f5f5f5',
      color: 'rgba(0, 0, 0, 0.26)',
      borderColor: '#e0e0e0',
      cursor: 'not-allowed',
    },
  };

  //For pagination
  const [paginationCount, setPaginationCount] = useState(0);
  const [pageState, setPageState] = useState({
    isLoading: true,
    data: [],
    total: 0,
    page: 1,
    pageSize: 25,
  });

  useEffect(() => {
    if (pageState?.total >= 5) {
      setPaginationCount(Math.floor(pageState.total / pageState?.pageSize));
    } else setPaginationCount(0);
  }, [pageState?.total, pageState?.pageSize]);

  const getLockedUnlockedCashAllocationRecords = useCallback(async () => {
    try {
      // set loading indicator
      setPageState((old) => ({
        ...old,
        isLoading: true,
      }));

      // Calculate skip number (skip 0 means page 1)
      const skipNumber = pageState?.page === 1
        ? 0
        : pageState?.page === 0
        ? pageState?.page
        : pageState?.page - 1;

      const response = await instance.get(
        `${getCashAllocationLockedUnlocked}?skip=${skipNumber}&pageSize=${
          pageState?.pageSize
        }&user_id=${userData?.id}`
      );

      // set loading indicator
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));

      if (response?.data) {
        setPageState((old) => ({
          ...old,
          total: response?.data?.count,
          data: response?.data?.results ?? [],
        }));
        setPaginationCount(
          Math.floor(response?.data?.count / pageState?.pageSize)
        );
      } else {
        setPageState((old) => ({
          ...old,
          total: response?.data?.count,
          data: [],
        }));
        setPaginationCount(0);
      }
    } catch (err) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      setPaginationCount(0);
      console.error("err", err);
    }
  }, [userData?.id, pageState?.pageSize, pageState?.page]);

  useEffect(() => {
    if (userData?.id) {
      getLockedUnlockedCashAllocationRecords();
    }
  }, [userData?.id, pageState?.pageSize, pageState?.page, getLockedUnlockedCashAllocationRecords]);

  const isFilterBtnDisable = () => {
    let disabled = false;
    if (fromDateValue === "" || toDateValue === "") {
      disabled = true;
    }
    return disabled;
  };

  const handleCommentsCloseDialog = () => {
    setOpenDialog(false);
    setDialogTextAreaChangeValue("");
  };

  const handleDialogInputChange = (value) => {
    setDialogTextAreaChangeValue(value);
  };

  const handleSaveComments = async () => {
    const unLockModeObj = {
      user_id: userData?.id,
      id: currentClickedRow?.id,
      comment: dialogTextAreaChangeValue || "",
    };
    try {
      const response = await instance.patch(
        getCashAllocationLockedUnlocked,
        unLockModeObj
      );
      if (response) {
        getLockedUnlockedCashAllocationRecords();
        commonToast("success", `UnLocked successfully !`);
        setUnlockRecordCountTextMessage(response?.data?.message);
        setOpenDialog(false);
        setDialogTextAreaChangeValue("");
      } else {
        commonToast("error", "Unable to UnLock Allocations");
      }
    } catch (err) {
      console.log(err);
      commonToast("error", "Unable to UnLock Allocations");
    }
  };

  const handleActionLockDialog = async () => {
    const lockModeObj = {
      user_id: userData?.id,
      allocation_datetime: `${format(fromDateValue, "yyyy-MM-dd")}_${format(
        toDateValue,
        "yyyy-MM-dd"
      )}`,
      locked: true,
    };
    try {
      const response = await instance.patch(
        cashAllocationLockedUnlocked,
        lockModeObj
      );
      setLockStateResponse(response?.data?.message);
      if (response) {
        getLockedUnlockedCashAllocationRecords();

        commonToast("success", `Locked successfully !`);
        setDialogOpen(false);
        setFromDateValue("");
        setToDateValue("");
      } else {
        commonToast("error", "Unable to Lock Allocations");
      }
    } catch (err) {
      console.log(err);
      commonToast("error", "Unable to Lock Allocations");
    }
  };

  const handleUnlockRecord = async (params) => {
    setOpenDialog(true);
    setCurrentClickedRow(params);
  };

  const handleSubmitButton = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const clearData = () => {
    setFromDateValue("");
    setToDateValue("");
  };

  const handleCancelButton = () => {
    clearData();
  };

  const handleApplyFilters = () => {
    // This will trigger the lock dialog if dates are selected
    if (!isFilterBtnDisable()) {
      handleSubmitButton();
    }
  };

  const handleClearFilters = () => {
    clearData();
  };

  const columns = [
    {
      field: "date_range",
      headerName: "Date Range",
      headerAlign: "center",
      flex: 0.6,
      renderCell: (params) => {
        const getDate = params?.row?.date_range.split("_");
        const startDate = getDate[0];
        const endDate = getDate[1];
        return (
          <span>{`${customDateFormat(startDate, "dd-MM-yyyy")} - ${customDateFormat(
            endDate,
            "dd-MM-yyyy"
          )}`}</span>
        );
      },
    },
    {
      field: "locked",
      headerName: "Lock / Unlock",
      headerAlign: "center",
      flex: 0.5,
      renderCell: (params) => {
        const isLocked = params?.row?.locked;
        return (
          <Chip
            label={isLocked ? "Locked" : "Unlocked"}
            sx={{
              backgroundColor: isLocked 
                ? "rgba(244, 67, 54, 0.1)" 
                : "rgba(76, 175, 80, 0.1)",
              color: isLocked ? "#f44336" : "#4caf50",
              fontWeight: "600",
              fontSize: "12px",
              height: "28px",
              borderRadius: "6px",
              border: `1px solid ${isLocked ? "#f44336" : "#4caf50"}`,
            }}
          />
        );
      },
    },
    {
      field: "no_of_record",
      headerName: "Count of Txn",
      headerAlign: "center",
      flex: 0.5,
      renderCell: (params) => <span>{params?.row?.no_of_record}</span>,
    },
    {
      field: "locked_unlocked_by_username",
      headerName: "Initiate by",
      headerAlign: "center",
      flex: 0.5,
    },
    {
      field: "locked_unlocked_date",
      headerName: "Locked / Unlocked Date",
      headerAlign: "center",
      flex: 0.6,
      renderCell: (params) => {
        return customDateFormat(params?.row?.locked_unlocked_date);
      },
    },
    {
      field: "comments",
      headerName: "Comments",
      headerAlign: "center",
      flex: 0.5,
      renderCell: (params) => <span>{params?.row?.comment}</span>,
    },
    ...(userData?.role !== "CC Processor" ? [{
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      flex: 0.5,
      renderCell: (params) => {
        const isDisabled = params?.row?.comment;
        return (
          <Chip
            label="Unlock"
            onClick={() => !isDisabled && handleUnlockRecord(params)}
            disabled={isDisabled}
            sx={{
              backgroundColor: isDisabled 
                ? "rgba(204, 204, 204, 0.3)" 
                : "rgba(22, 114, 222, 0.1)",
              color: isDisabled ? "#999" : "#1672DE",
              fontWeight: "600",
              fontSize: "12px",
              height: "32px",
              borderRadius: "8px",
              border: `1px solid ${isDisabled ? "#e0e0e0" : "#1672DE"}`,
              cursor: isDisabled ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: isDisabled 
                  ? "rgba(204, 204, 204, 0.3)" 
                  : "rgba(22, 114, 222, 0.2)",
                borderColor: isDisabled ? "#e0e0e0" : "#1565c0",
                transform: isDisabled ? "none" : "translateY(-2px)",
                boxShadow: isDisabled 
                  ? "none" 
                  : "0 4px 8px rgba(22, 114, 222, 0.2)",
              },
              "&:active": {
                transform: isDisabled ? "none" : "translateY(0)",
              },
              "& .MuiChip-label": {
                padding: "0 12px",
              },
            }}
          />
        );
      },
    }] : []),
  ];

  return (
    <>
      <style>
        {`
          /* Override DataGrid column headers to blue theme */
          .MuiDataGrid-root .MuiDataGrid-columnHeaders {
            background-color: #f8f9fa !important;
            border-bottom: 2px solid #e0e0e0 !important;
          }
          .MuiDataGrid-root .MuiDataGrid-columnHeader {
            background-color: transparent !important;
            color: #1672DE !important;
          }
          /* Override DataGrid selection colors to blue - override MUI theme primary color */
          .MuiDataGrid-root .MuiDataGrid-row.Mui-selected,
          .MuiDataGrid-root .MuiDataGrid-row[aria-selected="true"] {
            background-color: rgba(22, 114, 222, 0.08) !important;
          }
          .MuiDataGrid-root .MuiDataGrid-row.Mui-selected:hover,
          .MuiDataGrid-root .MuiDataGrid-row[aria-selected="true"]:hover {
            background-color: rgba(22, 114, 222, 0.12) !important;
          }
          .MuiDataGrid-root .MuiDataGrid-row:hover {
            background-color: rgba(22, 114, 222, 0.04) !important;
          }
          /* Override any orange/yellow colors from theme */
          .MuiDataGrid-root .MuiDataGrid-columnHeaders[style*="#FF"],
          .MuiDataGrid-root .MuiDataGrid-columnHeader[style*="#FF"],
          .MuiDataGrid-root .MuiDataGrid-row.Mui-selected[style*="#FF"],
          .MuiDataGrid-root .MuiDataGrid-row.Mui-selected[style*="ff5"] {
            background-color: rgba(22, 114, 222, 0.08) !important;
          }
        `}
      </style>
      <div style={Container}>
      {/* Filter Sidebar */}
      <CommonFilterSidebar
        isOpen={filterSidebarOpen}
        filterConfigs={[
          {
            type: "dateRange",
            label: "Accounting Month Range",
            startDate: fromDateValue,
            setStartDate: setFromDateValue,
            endDate: toDateValue,
            setEndDate: setToDateValue,
            maxDate: new Date(),
          },
        ]}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        isLoading={pageState?.isLoading}
        isFilterDisabled={isFilterBtnDisable()}
      />

      {/* Main Content Area */}
      <Box className="main-content-area">
        <Box className="page-header-container">
          <Typography variant="h5" className="page-heading">
          Locking Period
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <CommonButton
            text="Lock"
            handleClick={() => handleSubmitButton()}
              sx={lockButtonStyle}
              hoverColor="#1565c0"
            disabled={isFilterBtnDisable()}
          />
          <CommonButton
            text="Cancel"
            handleClick={() => handleCancelButton()}
              sx={cancelButtonStyle}
            disabled={isFilterBtnDisable()}
          />
            <IconButton
              onClick={() => setFilterSidebarOpen(!filterSidebarOpen)}
              className="filter-toggle-button"
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        {lockStateResponse && (
          <Box
            sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
              marginBottom: "10px",
        }}
      >
        <Typography
              variant="body1"
              sx={{ fontWeight: "600", fontSize: 16, color: "#1672DE" }}
        >
          {lockStateResponse}
        </Typography>
          </Box>
        )}

        {unlockRecordCountTextMessage && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              marginBottom: "10px",
            }}
          >
        <Typography
              variant="body1"
              sx={{ fontWeight: "600", fontSize: 16, color: "#1672DE" }}
        >
          {unlockRecordCountTextMessage}
        </Typography>
      </Box>
        )}

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            position: "relative",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            overflow: "hidden",
            transition: "all 0.3s ease",
            border: "1px solid #e8eaf0",
            "&:hover": {
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            },
          }}
        >
        <DataGrid
          className={classes.pagination}
          loading={pageState?.isLoading}
          rows={pageState?.data ?? []}
          columns={columns}
          rowCount={pageState?.total}
          keepNonExistentRowsSelected
          disableSelectionOnClick
          pagination
          paginationMode="server"
          getRowId={(row) => row?.id}
          page={pageState.page - 1}
          pageSize={pageState?.pageSize ?? 25}
            pageSizeOptions
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageSizeChange={(newPageSize) => {
            return setPageState((old) => {
              setPaginationCount(Math.floor(pageState?.total / newPageSize));
              return { ...old, pageSize: newPageSize, page: 1 };
            });
          }}
            componentsProps={{
              row: {
                sx: {
                  "&.Mui-selected": {
                    backgroundColor: "rgba(22, 114, 222, 0.08) !important",
                    "&:hover": {
                      backgroundColor: "rgba(22, 114, 222, 0.12) !important",
                    },
                  },
                },
              },
          }}
          sx={{
              border: "none",
              flex: 1,
              minHeight: 0,
              "& .MuiDataGrid-root": {
                border: "none",
                height: "100%",
              },
              "& .MuiDataGrid-main": {
                height: "100%",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#1672DE",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "#1565c0",
                  },
                },
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #e0e0e0",
                minHeight: "52px !important",
                maxHeight: "52px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "transparent",
                fontWeight: "600",
                color: "#1672DE",
                fontSize: "14px",
                padding: "12px 16px",
                borderBottom: "none",
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "600",
                  fontSize: "14px",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#1672DE",
                },
                "& .MuiDataGrid-menuIcon": {
                  color: "#1672DE",
                },
              },
            "& .MuiDataGrid-columnHeader[data-field='actions']": {
              minWidth: toggle?.isOpen
                ? "175.137px !important"
                : "203px !important",
            },
              // Override default orange/yellow selection colors to blue - override MUI theme
              "& .MuiDataGrid-row": {
                transition: "all 0.2s ease",
                borderBottom: "1px solid #f0f0f0",
                "&:hover": {
                  backgroundColor: "rgba(22, 114, 222, 0.04) !important",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(22, 114, 222, 0.08) !important",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.12) !important",
                  },
                },
                "&[aria-selected='true']": {
                  backgroundColor: "rgba(22, 114, 222, 0.08) !important",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.12) !important",
                  },
                },
                "&:nth-of-type(even)": {
                  backgroundColor: "#fafafa",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.04) !important",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(22, 114, 222, 0.08) !important",
                  },
                  "&[aria-selected='true']": {
                    backgroundColor: "rgba(22, 114, 222, 0.08) !important",
                  },
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f0f0f0",
                padding: "12px 16px",
                fontSize: "13px",
                color: "#333",
                display: "flex",
                alignItems: "center",
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#1672DE",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "#1565c0",
                  },
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "2px solid #e0e0e0",
                borderBottom: "none",
                backgroundColor: "#f8f9fa",
                minHeight: "56px",
                maxHeight: "56px",
                paddingBottom: "8px",
                paddingTop: "8px",
                paddingLeft: "16px",
                paddingRight: "16px",
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                "& .MuiTablePagination-root": {
                  color: "#666",
                  paddingRight: "16px",
                  margin: 0,
                  minHeight: "32px",
                },
                "& .MuiTablePagination-selectLabel": {
                  fontSize: "13px",
                  margin: 0,
                },
              },
          }}
          components={{
              Toolbar: GridToolbarContainer,
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No CashAllocation Records
              </Stack>
            ),
          }}
        />
        <Stack
          sx={{
            position: "absolute",
            bottom: "12px",
            right: "16px",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <CommonPaginationContainer
            count={paginationCount + 1}
            pageState={pageState}
            setPageState={setPageState}
          />
        </Stack>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(22, 114, 222, 0.15)",
            minWidth: "400px",
            maxWidth: "500px",
          },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(22, 114, 222, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#ffffff",
            color: "#1672DE",
            padding: "20px 24px",
            fontWeight: "600",
            fontSize: "20px",
            margin: 0,
            borderBottom: "none",
          }}
        >
          Confirmation
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "#ffffff",
            padding: "24px",
            paddingTop: "0px",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              color: "#333",
              fontWeight: "400",
              lineHeight: "1.6",
            }}
          >
            Are you sure that you want to Lock the processing for the
            "Allocated" transactions
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#ffffff",
            padding: "16px 24px",
            borderTop: "1px solid #e0e0e0",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <CommonButton
            text="No"
            handleClick={handleCloseDialog}
            sx={{
              backgroundColor: "transparent",
              color: "#1672DE",
              border: "1px solid #1672DE",
              borderRadius: "10px",
              padding: "10px 24px",
              fontWeight: "600",
              fontSize: "14px",
              textTransform: "none",
              minHeight: "40px",
              minWidth: "120px",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(22, 114, 222, 0.05)",
                borderColor: "#1565c0",
              },
            }}
          />
          <CommonButton
            text="Yes"
            handleClick={handleActionLockDialog}
            sx={{
              backgroundColor: "#1672DE",
              color: "white",
              borderRadius: "10px",
              padding: "10px 24px",
              fontWeight: "600",
              fontSize: "14px",
              textTransform: "none",
              minHeight: "40px",
              minWidth: "120px",
              boxShadow: "0 2px 8px rgba(22, 114, 222, 0.2)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#1565c0",
                boxShadow: "0 4px 12px rgba(22, 114, 222, 0.3)",
              },
            }}
          />
        </DialogActions>
      </Dialog>
      {/* Comments Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCommentsCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(22, 114, 222, 0.15)",
            minWidth: "400px",
            maxWidth: "500px",
          },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(22, 114, 222, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#ffffff",
            color: "#1672DE",
            padding: "20px 24px",
            fontWeight: "600",
            fontSize: "20px",
            margin: 0,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          Comments
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "#ffffff",
            padding: "24px",
            paddingTop: "24px",
          }}
        >
          <TextareaAutosize
            minRows={5}
            onChange={(e) => handleDialogInputChange(e.target.value)}
            className="PaymentReferenceDialogue"
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #d0d0d0",
              fontSize: "14px",
              fontFamily: "inherit",
              resize: "vertical",
              "&:focus": {
                outline: "none",
                borderColor: "#1672DE",
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#ffffff",
            padding: "16px 24px",
            borderTop: "1px solid #e0e0e0",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <CommonButton
            text="Cancel"
            handleClick={handleCommentsCloseDialog}
            sx={{
              backgroundColor: "transparent",
              color: "#1672DE",
              border: "1px solid #1672DE",
              borderRadius: "10px",
              padding: "10px 24px",
              fontWeight: "600",
              fontSize: "14px",
              textTransform: "none",
              minHeight: "40px",
              minWidth: "120px",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(22, 114, 222, 0.05)",
                borderColor: "#1565c0",
              },
            }}
          />
          <CommonButton
            text="Submit"
            handleClick={handleSaveComments}
            sx={{
              backgroundColor: "#1672DE",
              color: "white",
              borderRadius: "10px",
              padding: "10px 24px",
              fontWeight: "600",
              fontSize: "14px",
              textTransform: "none",
              minHeight: "40px",
              minWidth: "120px",
              boxShadow: "0 2px 8px rgba(22, 114, 222, 0.2)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#1565c0",
                boxShadow: "0 4px 12px rgba(22, 114, 222, 0.3)",
              },
              "&:disabled": {
                backgroundColor: "#CCCCCC",
                color: "#ffffff",
                boxShadow: "none",
                cursor: "not-allowed",
              },
            }}
            disabled={dialogTextAreaChangeValue === ""}
          />
        </DialogActions>
      </Dialog>
    </div>
    </>
  );
};
